/**
 * Shared utilities for campaign epoch calculations.
 * Used by both internal-cms (UI badges) and API (HubSpot integration).
 */

/**
 * Parse a flexible number string that may contain:
 * - Dollar signs ($2,000)
 * - Thousand separators (1,000,000)
 * - European-style decimals (2,5)
 * - Standard decimals (2.5)
 *
 * @param value - The value to parse
 * @returns The parsed number, or undefined if invalid
 */
export const parseFlexibleNumber = (value: string | number | null | undefined): number | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const sanitized = trimmed.replace(/[^0-9.,-]/g, "");
  if (!sanitized) {
    return undefined;
  }

  const hasComma = sanitized.includes(",");
  const hasDot = sanitized.includes(".");

  let normalized: string;

  if (hasComma && hasDot) {
    // Both comma and dot: comma is thousand separator (e.g., "1,000.50")
    normalized = sanitized.replace(/,/g, "");
  } else if (hasComma) {
    // Only comma: detect if thousand separator or decimal
    // Thousand separator: comma followed by exactly 3 digits (e.g., "2,000" or "1,000,000")
    // Decimal: comma followed by 1-2 digits at end (e.g., "2,5" or "2,50")
    const isThousandSeparator = /,\d{3}(?:,|$)/.test(sanitized);
    normalized = isThousandSeparator ? sanitized.replace(/,/g, "") : sanitized.replace(",", ".");
  } else {
    normalized = sanitized;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Convert epoch duration from milliseconds to hours.
 */
export const getEpochDurationHours = (epochDurationMs: number | undefined): number =>
  epochDurationMs && epochDurationMs > 0 ? epochDurationMs / (1000 * 60 * 60) : 0;

/**
 * Calculate the number of epochs between start and end dates.
 */
export const getEpochCount = (
  start: Date | string | number | null | undefined,
  end: Date | string | number | null | undefined,
  epochDurationMs: number | undefined,
): number => {
  if (!epochDurationMs || epochDurationMs <= 0 || !start || !end) {
    return 0;
  }

  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return 0;
  }

  return Math.floor((endMs - startMs) / epochDurationMs);
};

/**
 * Calculate expected volume per epoch based on daily expected volume.
 * Result is floored to a whole number.
 */
export const calcExpectedVolumePerEpoch = (
  expectedDaily: number | undefined,
  epochDurationHours: number,
): number | undefined => {
  if (expectedDaily === undefined || expectedDaily < 0 || epochDurationHours <= 0) {
    return undefined;
  }
  return Math.floor(expectedDaily * (epochDurationHours / 24));
};

/**
 * Calculate total reward per epoch based on prize pool and epoch count.
 * Result is floored to 2 decimal places.
 */
export const calcTotalRewardPerEpoch = (prizePool: number | undefined, epochCount: number): number | undefined => {
  if (prizePool === undefined || prizePool <= 0 || epochCount <= 0) {
    return undefined;
  }
  // Floor to 2 decimal places
  return Math.floor((prizePool / epochCount) * 100) / 100;
};

/**
 * Format a number for display in suggestion badges.
 * Floors to 2 decimal places and removes trailing zeros.
 */
export const formatSuggestionNumber = (value: number): string => {
  if (!Number.isFinite(value)) return "";
  // Floor to 2 decimal places
  const floored = Math.floor(value * 100) / 100;
  if (Number.isInteger(floored)) return floored.toString();
  return floored.toFixed(2).replace(/\.?0+$/, "");
};
